import { Server, ServerAllocation } from '@/types'
import { serverLogger } from './debug'

// Helper functions to work with server data
// Panel's client resource route uses the short identifier shown in server links.
// Keep the batch request key and response lookup key identical.
export const panelResourceIdentifier = (server: Pick<Server, 'identifier' | 'uuid'>): string =>
  server.identifier || server.uuid

// Administrative blocks are known before live resources are requested. A null
// Panel status is eligible for observation, never evidence that it is running.
type ServerObservationState = {
  status?: string | null; is_suspended?: boolean; is_installing?: boolean
  is_transferring?: boolean; isTransferring?: boolean
} | null | undefined

// Installation output uses the console socket, even though live resources are
// not available yet. Administrative blocks must stop both connection paths.
export const canConnectServerConsole = (server: ServerObservationState): boolean => Boolean(server
  && !server.is_suspended && !server.is_transferring && !server.isTransferring
  && !['suspended', 'install_failed', 'reinstall_failed', 'restoring_backup', 'transferring'].includes(server.status || ''))

export const canObserveServerResources = (server: ServerObservationState): boolean =>
  canConnectServerConsole(server) && !server?.is_installing && server?.status !== 'installing'

export const getServerStatus = (server: Server) => {
  if (server?.is_suspended === true) return 'suspended'
  if (server?.is_installing === true) return 'installing'

  // Prefer live current_state from /resources over attributes.status from list
  const liveState: string | undefined = (server as any)?._state || (server as any)?._live?.current_state
  const state = (liveState || server?.status || '').toString().toLowerCase()
  if (state === 'running') return 'running'
  if (state === 'starting') return 'starting'
  if (state === 'stopping') return 'stopping'
  if (state === 'offline') return 'offline'
  return 'offline'
}

export const getServerStatusColor = (server: Server) => {
  const status = getServerStatus(server)
  switch (status) {
  case 'suspended': return 'bg-red-100 text-red-800 dark:bg-red-500/15 dark:text-red-300'
  case 'installing': return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-500/15 dark:text-yellow-300'
  case 'running': return 'bg-green-100 text-green-800 dark:bg-green-500/15 dark:text-green-300'
  case 'starting': return 'bg-blue-100 text-blue-800 dark:bg-blue-500/15 dark:text-blue-300'
  case 'stopping': return 'bg-orange-100 text-orange-800 dark:bg-orange-500/15 dark:text-orange-300'
  default: return 'bg-gray-100 text-gray-800 dark:bg-slate-600/40 dark:text-gray-200'
  }
}

export const getServerStatusText = (server: Server) => {
  const status = getServerStatus(server)
  switch (status) {
    case 'suspended': return 'Suspended'
    case 'installing': return 'Installing'
    case 'running': return 'Online'
    case 'starting': return 'Starting'
    case 'stopping': return 'Stopping'
    default: return 'Offline'
  }
}

export const getServerStatusChipClass = (server: Server) => {
  const status = getServerStatus(server)
  switch (status) {
    case 'suspended': return 'status-chip status-suspended'
    case 'installing': return 'status-chip status-installing'
    case 'running': return 'status-chip status-running'
    case 'starting': return 'status-chip status-starting'
    case 'stopping': return 'status-chip status-stopping'
    default: return 'status-chip status-offline'
  }
}


export const isServerRunning = (server: Server) => {
  return getServerStatus(server) === 'running'
}

export const getServerAllocation = (server: Server): ServerAllocation | null => {
  // Handle the API response structure where allocations are nested
  const relAlloc = server?.relationships?.allocations?.data
  const relAllocFlat = (server as any)?.relationships?.allocations
  const flatAlloc = (server as any)?.allocations
  const enriched = (server as any)?._allocations // optional enrichment from batch fetch
  
  let allocations: any[] = []
  
  if (Array.isArray(relAlloc)) {
    // API returns nested structure: { object: "allocation", attributes: {...} }
    allocations = relAlloc.map(alloc => alloc.attributes || alloc)
  } else if (Array.isArray(relAllocFlat)) {
    // API client sometimes transforms relationships.allocations into a flat array
    allocations = relAllocFlat.map((alloc: any) => alloc?.attributes || alloc)
  } else if (Array.isArray(flatAlloc)) {
    // Fallback to flat structure
    allocations = flatAlloc
  } else if (Array.isArray(enriched)) {
    allocations = enriched
  }

  if (!allocations.length) return null
  
  const preferred = allocations.find((a: any) => a.is_default === true)
  const result = preferred ?? allocations[0]
  
  return result ? {
    id: result.id,
    ip: result.ip,
    ip_alias: result.ip_alias,
    port: result.port,
    notes: result.notes,
    is_default: result.is_default
  } : null
}

// Get the properly formatted address for display (following Pterodactyl pattern)
export const getServerAddress = (server: Server): string => {
  // Note: Server list API doesn't include allocations by design (following Pterodactyl pattern)
  // Individual server pages should use the individual server API which includes allocations
  
  if (serverLogger.isDebugEnabled) {
    serverLogger.debug('getServerAddress called with:', {
      serverName: server?.name,
      serverUuid: server?.uuid,
      hasRelationships: !!server?.relationships,
      allocationsPath: server?.relationships?.allocations,
      allocationsData: server?.relationships?.allocations?.data,
      _allocations: (server as any)?._allocations,
      _allocationsLength: Array.isArray((server as any)?._allocations) ? (server as any)._allocations.length : 0,
      serverKeys: Object.keys(server || {}),
      relationshipKeys: server?.relationships ? Object.keys(server.relationships) : 'no relationships'
    })
  }
  
  // Handle different possible allocation structures
  let allocationsData: any[] = []
  
  // Check if it's the nested structure: relationships.allocations.data
  if (server?.relationships?.allocations?.data && Array.isArray(server.relationships.allocations.data)) {
    allocationsData = server.relationships.allocations.data
    serverLogger.debug('Using nested structure: relationships.allocations.data')
  }
  // Check if allocations is directly an array (which is what our API client transforms to)
  else if (Array.isArray(server?.relationships?.allocations)) {
    allocationsData = server.relationships.allocations
    serverLogger.debug('Using flat structure: relationships.allocations (API client transformed)')
  }
  // Fallback to legacy _allocations enrichment (no longer used but kept for backward compatibility)
  else if (Array.isArray((server as any)?._allocations)) {
    allocationsData = (server as any)._allocations
    serverLogger.debug('Using legacy _allocations enrichment')
  }
  // Fallback to flat allocations property
  else if (Array.isArray((server as any)?.allocations)) {
    allocationsData = (server as any).allocations
    serverLogger.debug('Using legacy flat structure: server.allocations')
  }
  
  serverLogger.debug('Found allocations data, count:', allocationsData.length, 'first item:', allocationsData[0])
  
  if (!allocationsData.length) {
    serverLogger.debug('No allocations data found - this is expected for server list API, use individual server API for allocations')
    return '—'
  }
  
  // Get the first allocation (or find the default one)
  const defaultAllocation = allocationsData.find(alloc => {
    // Handle both nested attributes structure and flat structure
    const attrs = alloc?.attributes || alloc
    return attrs?.is_default === true
  }) || allocationsData[0]
  
  serverLogger.debug('Selected allocation:', defaultAllocation)
  
  // Handle both nested attributes structure and flat structure
  const attrs = defaultAllocation?.attributes || defaultAllocation
  
  if (!attrs || (!attrs.ip_alias && !attrs.ip)) {
    serverLogger.debug('No allocation attributes found, returning —', { attrs })
    return '—'
  }
  
  // Prefer ip_alias when present (user-friendly DNS or mapped address); fallback to raw ip
  const displayIP = attrs.ip_alias && typeof attrs.ip_alias === 'string' && attrs.ip_alias.trim().length
    ? attrs.ip_alias.trim()
    : attrs.ip
  const result = `${displayIP}:${attrs.port}`
  serverLogger.debug('Returning address (connection column):', { result, ip_alias: attrs.ip_alias, ip: attrs.ip })
  return result
}

// Debug function to help troubleshoot allocation issues
export const debugServerAllocation = (server: Server): any => {
  serverLogger.debug('Debug server allocation:', {
    server: server.name,
    relationships: server?.relationships,
    allocationsData: server?.relationships?.allocations?.data,
    flatAllocations: (server as any)?.allocations,
    enriched: (server as any)?._allocations
  })
  return getServerAllocation(server)
}

export const formatBytes = (bytes: number) => {
  if (!Number.isFinite(bytes) || bytes <= 0) return '0 B'
  const k = 1024
  const sizes = ['B', 'KB', 'MB', 'GB', 'TB', 'PB']
  const i = Math.min(sizes.length - 1, Math.max(0, Math.floor(Math.log(bytes) / Math.log(k))))
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i]
}

// Deterministic utilization fallback (no random values). Replaced by live data when available.
export const getServerUtilization = (_server: Server) => ({ cpu: 0, memory: 0, disk: 0 })

// Panel returns limits.memory/disk in megabytes (MB). Convert to bytes for UI comparison.
export const mbToBytes = (mb: number): number => Math.max(0, Math.floor((mb || 0) * 1024 * 1024))

export const formatLimit = (mb: number): string => (mb === 0 ? 'Unlimited' : formatBytes(mbToBytes(mb)))

// Server type detection and icon colors
export interface ServerTypeInfo {
  type: string
  color: string // Tailwind color class
  bgColor: string // Background color class
  icon?: string // Icon path or type
}

export const getServerTypeInfo = (server: Server): ServerTypeInfo => {
  const dockerImage = server?.docker_image?.toLowerCase() || ''
  const name = server?.name?.toLowerCase() || ''
  
  // Check for database servers first
  if (server?.is_database_server || server?.database_engine) {
    switch (server.database_engine) {
      case 'mysql':
        return { type: 'MySQL', color: 'text-blue-600 dark:text-blue-400', bgColor: 'bg-blue-100 dark:bg-blue-500/20' }
      case 'postgresql':
        return { type: 'PostgreSQL', color: 'text-blue-700 dark:text-blue-400', bgColor: 'bg-blue-100 dark:bg-blue-500/20' }
      case 'mongodb':
        return { type: 'MongoDB', color: 'text-green-600 dark:text-green-400', bgColor: 'bg-green-100 dark:bg-green-500/20' }
      case 'redis':
        return { type: 'Redis', color: 'text-red-600 dark:text-red-400', bgColor: 'bg-red-100 dark:bg-red-500/20' }
    }
  }
  
  // Game servers - check docker image and name
  if (dockerImage.includes('minecraft') || name.includes('minecraft')) {
    return { type: 'Minecraft', color: 'text-green-600 dark:text-green-400', bgColor: 'bg-green-100 dark:bg-green-500/20' }
  }
  if (dockerImage.includes('rust') || name.includes('rust')) {
    return { type: 'Rust', color: 'text-orange-600 dark:text-orange-400', bgColor: 'bg-orange-100 dark:bg-orange-500/20' }
  }
  if (dockerImage.includes('palworld') || name.includes('palworld')) {
    return { type: 'Palworld', color: 'text-pink-600 dark:text-pink-400', bgColor: 'bg-pink-100 dark:bg-pink-500/20' }
  }
  if (dockerImage.includes('valheim') || name.includes('valheim')) {
    return { type: 'Valheim', color: 'text-purple-600 dark:text-purple-400', bgColor: 'bg-purple-100 dark:bg-purple-500/20' }
  }
  if (dockerImage.includes('terraria') || name.includes('terraria')) {
    return { type: 'Terraria', color: 'text-lime-600 dark:text-lime-400', bgColor: 'bg-lime-100 dark:bg-lime-500/20' }
  }
  if (dockerImage.includes('csgo') || dockerImage.includes('counter-strike') || name.includes('csgo')) {
    return { type: 'CS:GO', color: 'text-yellow-600 dark:text-yellow-400', bgColor: 'bg-yellow-100 dark:bg-yellow-500/20' }
  }
  if (dockerImage.includes('ark') || name.includes('ark')) {
    return { type: 'ARK', color: 'text-cyan-600 dark:text-cyan-400', bgColor: 'bg-cyan-100 dark:bg-cyan-500/20' }
  }
  if (dockerImage.includes('factorio') || name.includes('factorio')) {
    return { type: 'Factorio', color: 'text-amber-600 dark:text-amber-400', bgColor: 'bg-amber-100 dark:bg-amber-500/20' }
  }
  if (dockerImage.includes('teamspeak') || name.includes('teamspeak')) {
    return { type: 'TeamSpeak', color: 'text-indigo-600 dark:text-indigo-400', bgColor: 'bg-indigo-100 dark:bg-indigo-500/20' }
  }
  
  // Database images (for non-database servers using database containers)
  if (dockerImage.includes('mariadb') || dockerImage.includes('mysql')) {
    return { type: 'MariaDB', color: 'text-teal-600 dark:text-teal-400', bgColor: 'bg-teal-100 dark:bg-teal-500/20' }
  }
  if (dockerImage.includes('postgres')) {
    return { type: 'PostgreSQL', color: 'text-blue-700 dark:text-blue-400', bgColor: 'bg-blue-100 dark:bg-blue-500/20' }
  }
  if (dockerImage.includes('mongo')) {
    return { type: 'MongoDB', color: 'text-green-600 dark:text-green-400', bgColor: 'bg-green-100 dark:bg-green-500/20' }
  }
  if (dockerImage.includes('redis')) {
    return { type: 'Redis', color: 'text-red-600 dark:text-red-400', bgColor: 'bg-red-100 dark:bg-red-500/20' }
  }
  
  // Programming languages
  if (dockerImage.includes('node') || dockerImage.includes('nodejs')) {
    return { type: 'Node.js', color: 'text-green-700 dark:text-green-400', bgColor: 'bg-green-100 dark:bg-green-500/20' }
  }
  if (dockerImage.includes('python')) {
    return { type: 'Python', color: 'text-blue-600 dark:text-blue-400', bgColor: 'bg-blue-100 dark:bg-blue-500/20' }
  }
  
  // Default
  return { type: 'Generic', color: 'text-primary-600 dark:text-primary-400', bgColor: 'bg-primary-100 dark:bg-primary-500/20' }
}
